/**
 * How long the loan can run, worked out from what the money is for.
 *
 * An owner does not know their loan term — but they do know they are buying a
 * building, some equipment and a few months of payroll. SBA sets a maximum
 * maturity per use of proceeds and then blends them, so the term is derivable
 * rather than something to ask about. Deriving it is both more accurate and a
 * better question to ask.
 *
 * Two different regimes, and using the wrong one is a material error:
 *
 *   - Ordinary loans (Appendix 17). Each use has its own ceiling — 10 years for
 *     working capital, inventory and goodwill, 10 (or up to 15 on IRS useful
 *     life) for equipment, 25 for real estate. Mixed uses may be blended, OR,
 *     if real estate is at least 51% of proceeds, the whole loan may run 25
 *     years.
 *   - Changes of ownership (Appendix 15). The 51% option does not apply at all.
 *     Only the real-estate portion may exceed 10 years; everything else,
 *     explicitly including soft costs and working capital, is allocated 10
 *     years. The blend is rounded to the nearest full year and computed BEFORE
 *     any equity is applied.
 *
 * The result cascades: a maturity of 15 years or more brings SBA's subsidy
 * recoupment fee into play, and under 15 years there is none at all — which is
 * worth telling an owner plainly.
 *
 * Everything here is a CEILING. The SOP requires "the shortest appropriate term
 * based on the use of proceeds and the Borrower's ability to repay".
 */

import data from '../data/maturity-7a.json';
import type { Citation } from './types.js';

export type UseOfProceeds =
  | 'real-estate'
  | 'equipment'
  | 'leasehold-improvements'
  | 'working-capital'
  | 'inventory'
  | 'intangible-goodwill'
  | 'business-acquisition'
  | 'soft-costs';

/** What the money is for, in the owner's words. */
export const USE_LABELS: Record<UseOfProceeds, string> = {
  'real-estate': 'Commercial real estate',
  equipment: 'Equipment, fixtures or furniture',
  'leasehold-improvements': 'Leasehold improvements',
  'working-capital': 'Working capital',
  inventory: 'Inventory',
  'intangible-goodwill': 'Goodwill or other intangible assets',
  'business-acquisition': 'Buying the business',
  'soft-costs': 'Closing and other soft costs',
};

export interface Allocation {
  use: UseOfProceeds;
  amount: number;
}

export interface MaturityInput {
  allocations: Allocation[];
  /** Appendix 15 governs when true, and the 51% real-estate option is gone. */
  isChangeOfOwnership?: boolean;
  /**
   * IRS asset class useful life for the equipment, in years. Only lets
   * equipment run past 10 years, to a ceiling of 15; a shorter life does not
   * shorten the default.
   */
  equipmentUsefulLifeYears?: number;
  /**
   * Owner-occupied Special Purpose Property whose acquisition cannot be
   * separated from the business. Opens the 85% exemption on a change of
   * ownership.
   */
  specialPurposeProperty?: boolean;
  /** Farm enterprise, which has its own 20-year and 15-year majority rules. */
  isFarm?: boolean;
  /** Months to complete equipment installation or leasehold improvements. Max 12. */
  installationMonths?: number;
  /**
   * Months reasonably necessary for construction or renovation, added to real
   * estate's 25 years. SBA sets no numeric cap, so this is the lender's
   * judgement and is reported as such.
   */
  constructionMonths?: number;
}

export interface MaturityComponent {
  use: UseOfProceeds;
  label: string;
  amount: number;
  /** Share of the total, 0..1. */
  share: number;
  /** This use's maximum maturity in months. */
  maxTermMonths: number;
}

export type MaturityMethod =
  | 'single-use'
  | 'weighted-average'
  | 'majority-real-estate'
  | 'farm-majority-real-estate'
  | 'farm-majority-machinery'
  | 'special-purpose-property';

export interface MaturityResult {
  /** The maximum maturity SBA permits, in months. */
  maxTermMonths: number;
  /** The same in whole years, for talking to a lender. */
  maxTermYears: number;
  method: MaturityMethod;
  /** The unrounded weighted average, when one was computed. */
  weightedAverageMonths: number | null;
  components: MaturityComponent[];
  totalAmount: number;
  /** True when a 15-year-or-longer maturity brings subsidy recoupment into play. */
  subsidyRecoupmentApplies: boolean;
  /** Plain-English explanation, in the order an owner would want it. */
  notes: string[];
  citation: Citation;
}

const D = data as unknown as {
  source: string;
  citation: string;
  asOf: string;
  verified: boolean;
  general: {
    maxMonthsByUse: Record<string, number>;
    equipmentMaxMonthsWithUsefulLife: number;
    installationExtraMonthsMax: number;
    mixedPurpose: { majorityRealEstateThresholdShare: number; majorityRealEstateMaxMonths: number };
    farm: {
      landAndStructuresThresholdShare: number;
      landAndStructuresMaxMonths: number;
      machineryThresholdShare: number;
      machineryMaxMonths: number;
    };
  };
  changeOfOwnership: {
    defaultMaxMonths: number;
    realEstatePortionMaxMonths: number;
    roundToNearestMonths: number;
    specialPurposeProperty: { thresholdShare: number; maxMonths: number };
  };
  subsidyRecoupment: { minMaturityMonths: number };
};

const CITATION: Citation = {
  source: D.source,
  citation: D.citation,
  asOf: D.asOf,
  verified: D.verified,
};

const YEARS = (months: number) => Math.round((months / 12) * 10) / 10;

/** This use's ceiling, before any blending. */
function capFor(use: UseOfProceeds, input: MaturityInput): number {
  const install = Math.min(
    Math.max(input.installationMonths ?? 0, 0),
    D.general.installationExtraMonthsMax,
  );

  if (input.isChangeOfOwnership) {
    // Appendix 15: only real estate may exceed ten years.
    return use === 'real-estate'
      ? D.changeOfOwnership.realEstatePortionMaxMonths + Math.max(input.constructionMonths ?? 0, 0)
      : D.changeOfOwnership.defaultMaxMonths;
  }

  const base = D.general.maxMonthsByUse[use];
  if (base === undefined) throw new Error(`No maximum maturity on file for use "${use}"`);

  if (use === 'real-estate') return base + Math.max(input.constructionMonths ?? 0, 0);

  if (use === 'equipment') {
    const life = input.equipmentUsefulLifeYears;
    const extended =
      life !== undefined && life * 12 > base
        ? Math.min(life * 12, D.general.equipmentMaxMonthsWithUsefulLife)
        : base;
    return extended + install;
  }

  if (use === 'leasehold-improvements') return base + install;

  return base;
}

function validate(input: MaturityInput): void {
  if (!Array.isArray(input.allocations) || input.allocations.length === 0) {
    throw new Error('At least one use of proceeds is required');
  }
  for (const a of input.allocations) {
    if (!Number.isFinite(a.amount) || a.amount <= 0) {
      throw new Error(`Each use of proceeds needs a positive amount; got ${a.amount} for "${a.use}"`);
    }
    if (!(a.use in USE_LABELS)) throw new Error(`Unknown use of proceeds "${a.use}"`);
  }
  if (input.installationMonths !== undefined && input.installationMonths > D.general.installationExtraMonthsMax) {
    throw new Error(
      `SBA caps the installation period at ${D.general.installationExtraMonthsMax} months`,
    );
  }
}

/** Merge repeated uses so shares are computed once per use. */
function merge(allocations: Allocation[]): Allocation[] {
  const totals = new Map<UseOfProceeds, number>();
  for (const a of allocations) totals.set(a.use, (totals.get(a.use) ?? 0) + a.amount);
  return [...totals.entries()].map(([use, amount]) => ({ use, amount }));
}

/**
 * The maximum maturity for a set of uses, with the weighting shown so an owner
 * can see where the number came from.
 */
export function maxMaturity(input: MaturityInput): MaturityResult {
  validate(input);

  const allocations = merge(input.allocations);
  const totalAmount = allocations.reduce((s, a) => s + a.amount, 0);
  const components: MaturityComponent[] = allocations.map((a) => ({
    use: a.use,
    label: USE_LABELS[a.use],
    amount: a.amount,
    share: a.amount / totalAmount,
    maxTermMonths: capFor(a.use, input),
  }));

  const shareOf = (use: UseOfProceeds) =>
    components.find((c) => c.use === use)?.share ?? 0;
  const realEstateShare = shareOf('real-estate');
  const weighted = components.reduce((s, c) => s + c.share * c.maxTermMonths, 0);

  const notes: string[] = [];
  const finish = (
    months: number,
    method: MaturityMethod,
    weightedAverageMonths: number | null,
  ): MaturityResult => {
    const subsidy = months >= D.subsidyRecoupment.minMaturityMonths;
    notes.push(
      subsidy
        ? `At ${YEARS(
            months,
          )} years this loan carries SBA's prepayment fee: if you pay off more than 25% of it in any of the first three years, SBA charges 5% of that payment in year one, 3% in year two and 1% in year three.`
        : `Under 15 years, so there is no SBA prepayment fee on this loan — you could pay it off early at no penalty. Your lender may not add one of its own either.`,
    );
    notes.push(
      `This is the longest term SBA allows, not a recommendation: the rules require the shortest term your use of the money and your ability to repay support. A longer term means a lower payment but more interest.`,
    );
    return {
      maxTermMonths: months,
      maxTermYears: YEARS(months),
      method,
      weightedAverageMonths,
      components,
      totalAmount,
      subsidyRecoupmentApplies: subsidy,
      notes,
      citation: CITATION,
    };
  };

  // --- changes of ownership: Appendix 15 governs.
  if (input.isChangeOfOwnership) {
    notes.push(
      `Because this loan buys a business, SBA caps everything except the real-estate portion at 10 years.`,
    );

    if (
      input.specialPurposeProperty &&
      realEstateShare >= D.changeOfOwnership.specialPurposeProperty.thresholdShare
    ) {
      const months = D.changeOfOwnership.specialPurposeProperty.maxMonths;
      notes.push(
        `Real estate is ${Math.round(
          realEstateShare * 100,
        )}% of the project and the property is a special-purpose building you cannot separate from the business, so the whole loan may run up to ${YEARS(
          months,
        )} years.`,
      );
      return finish(months, 'special-purpose-property', weighted);
    }

    const round = D.changeOfOwnership.roundToNearestMonths;
    const months = Math.round(weighted / round) * round;
    if (components.length === 1) {
      notes.push(`All of it is for ${components[0]!.label.toLowerCase()}.`);
      return finish(months, 'single-use', weighted);
    }
    notes.push(
      `Blended across your uses by weighted average and rounded to the nearest full year, which is how SBA requires it. Worked out before your down payment is applied.`,
    );
    return finish(months, 'weighted-average', weighted);
  }

  // --- farm enterprises have their own majority rules.
  if (input.isFarm) {
    if (realEstateShare >= D.general.farm.landAndStructuresThresholdShare) {
      const months = D.general.farm.landAndStructuresMaxMonths;
      notes.push(
        `Farm land and structures are ${Math.round(
          realEstateShare * 100,
        )}% of the money, so SBA's 20-year farm maximum applies.`,
      );
      return finish(months, 'farm-majority-real-estate', weighted);
    }
    if (shareOf('equipment') >= D.general.farm.machineryThresholdShare) {
      const months = Math.min(
        D.general.farm.machineryMaxMonths,
        capFor('equipment', input),
      );
      notes.push(
        `Machinery and equipment are most of the money, so the term is its useful life, capped at ${YEARS(
          D.general.farm.machineryMaxMonths,
        )} years.`,
      );
      return finish(months, 'farm-majority-machinery', weighted);
    }
  }

  // --- the 51% real-estate option, which is never shorter than the blend.
  if (realEstateShare >= D.general.mixedPurpose.majorityRealEstateThresholdShare) {
    const months = D.general.mixedPurpose.majorityRealEstateMaxMonths +
      Math.max(input.constructionMonths ?? 0, 0);
    if (components.length === 1) {
      notes.push(`All of it is for commercial real estate, which SBA allows up to 25 years.`);
      return finish(months, 'single-use', null);
    }
    notes.push(
      `Real estate is ${Math.round(
        realEstateShare * 100,
      )}% of the money. Once it is at least 51%, SBA lets the whole loan run up to ${YEARS(
        months,
      )} years, which beats blending the uses (${YEARS(weighted)} years). Your lender chooses between the two.`,
    );
    return finish(months, 'majority-real-estate', weighted);
  }

  // --- otherwise blend.
  const months = Math.floor(weighted);
  if (components.length === 1) {
    notes.push(
      `All of it is for ${components[0]!.label.toLowerCase()}, which SBA allows up to ${YEARS(
        months,
      )} years.`,
    );
    return finish(months, 'single-use', null);
  }
  notes.push(
    `Blended across your uses by weighted average: ${components
      .map((c) => `${Math.round(c.share * 100)}% at ${YEARS(c.maxTermMonths)} years`)
      .join(', ')}.`,
  );
  return finish(months, 'weighted-average', weighted);
}
